import React from "react"
import { Users, Star, TrendingUp, Layers } from "lucide-react"

import SEO from "../../components/Common/SEO"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl } from "../../utils/seoUtils"

import InfluencerSection from "./components/InfluencerSection"
import { INFLUENCER_PLATFORMS, INFLUENCER_ACCENT_COLOR } from "./constants"

/**
 * Influencer Explorer - a standalone, top-level directory of every verified
 * TikTok, Instagram and YouTube creator, ranked in one cross-platform
 * leaderboard (TOP_INFLUENCER_FILTERS.md). Previously reached as a tab
 * inside UGC Explorer (via InfluencerBanner); it's now its own section with
 * its own menu entry and route (`/influencer-explorer`) since it's a
 * different shape of feature entirely - a creator directory, not one
 * platform's per-app video coverage - and never requires picking an app
 * first the way UGC Explorer's video tabs do.
 *
 * Keeps the same dark-card/Rubik-font/pink-accent (INFLUENCER_ACCENT_COLOR)
 * conventions as the rest of the app for visual continuity - it reads as a
 * distinct section through its own hero header and content, not through a
 * different color scheme.
 */
const InfluencerExplorer = () => {
    return (
        <React.Fragment>
            <SEO
                title={PAGE_SEO.influencerExplorer.title}
                description={PAGE_SEO.influencerExplorer.description}
                keywords={PAGE_SEO.influencerExplorer.keywords}
                canonical={generateCanonicalUrl('/influencer-explorer')}
                robots={PAGE_SEO.influencerExplorer.robots}
            />

            <div className="page-content">
                <div className="container-fluid" style={{ fontFamily: "Rubik, sans-serif" }}>

                    {/* ── Hero header ────────────────────────────────────── */}
                    <div className="influencer-hero mb-4 mt-2">
                        <span className="influencer-hero-glow" aria-hidden="true" />
                        <div className="influencer-hero-main">
                            <span className="influencer-hero-icon">
                                <Users size={26} color="#fff" />
                            </span>
                            <div className="influencer-hero-copy">
                                <div className="influencer-hero-heading">
                                    <h1>Influencer Explorer</h1>
                                    <span className="influencer-hero-badge">
                                        <Star size={10} fill={INFLUENCER_ACCENT_COLOR} /> Featured
                                    </span>
                                </div>
                                <p>
                                    Every verified TikTok, Instagram and YouTube creator in one ranked,
                                    cross-platform leaderboard - filter by audience size, app coverage,
                                    verification and more.
                                </p>
                                <div className="influencer-hero-stats">
                                    <span><TrendingUp size={12} /> Ranked by audience</span>
                                    <span><Layers size={12} /> 3 platforms, one list</span>
                                </div>
                            </div>
                        </div>

                        <div className="influencer-hero-platforms">
                            {INFLUENCER_PLATFORMS.map((platform) => (
                                <span
                                    key={platform.id}
                                    className="influencer-hero-platform-pill"
                                    style={{ borderColor: `${platform.color}55`, background: `${platform.color}14`, color: platform.color }}
                                >
                                    <i className={platform.icon} />
                                    {platform.label}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* ── Main content: the creator leaderboard ─────────── */}
                    <InfluencerSection />

                </div>
            </div>

            <style>{`
                .influencer-hero {
                    position: relative;
                    padding: 26px 28px;
                    border-radius: 20px;
                    border: 1px solid rgba(255,255,255,0.08);
                    background: linear-gradient(120deg, rgba(246,37,90,0.07), rgba(33,34,45,0.6) 55%, rgba(33,34,45,0.6));
                    overflow: hidden;
                }
                .influencer-hero-glow {
                    position: absolute;
                    top: -70px;
                    right: -50px;
                    width: 220px;
                    height: 220px;
                    border-radius: 50%;
                    background: radial-gradient(circle, rgba(246,37,90,0.16), transparent 70%);
                    pointer-events: none;
                }
                .influencer-hero-main {
                    display: flex;
                    align-items: center;
                    gap: 18px;
                    position: relative;
                    min-width: 0;
                }
                .influencer-hero-icon {
                    width: 52px;
                    height: 52px;
                    border-radius: 15px;
                    flex-shrink: 0;
                    background: #F6255A;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 8px 20px rgba(246,37,90,0.22);
                }
                .influencer-hero-copy { min-width: 0; }
                .influencer-hero-heading {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-wrap: wrap;
                    margin-bottom: 4px;
                }
                .influencer-hero-heading h1 {
                    color: #fff;
                    font-weight: 700;
                    font-size: 26px;
                    margin: 0;
                    font-family: Rubik, sans-serif;
                }
                .influencer-hero-badge {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 10.5px;
                    font-weight: 700;
                    color: #ffc2d1;
                    background: rgba(246,37,90,0.2);
                    padding: 2px 9px;
                    border-radius: 999px;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    white-space: nowrap;
                }
                .influencer-hero-copy p {
                    color: #c7c9d9;
                    font-size: 14px;
                    margin: 0;
                    max-width: 640px;
                    line-height: 1.55;
                }
                .influencer-hero-stats {
                    display: flex;
                    gap: 16px;
                    flex-wrap: wrap;
                    margin-top: 12px;
                }
                .influencer-hero-stats span {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    font-size: 11.5px;
                    color: #9ca3af;
                    font-weight: 500;
                }
                .influencer-hero-platforms {
                    display: flex;
                    gap: 10px;
                    flex-wrap: wrap;
                    margin-top: 20px;
                    position: relative;
                }
                .influencer-hero-platform-pill {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    padding: 6px 13px;
                    border-radius: 999px;
                    font-size: 12.5px;
                    font-weight: 600;
                    border: 1px solid;
                }
                @media (max-width: 700px) {
                    .influencer-hero { padding: 22px; }
                }
                @media (max-width: 480px) {
                    .influencer-hero { padding: 18px; border-radius: 16px; }
                    .influencer-hero-main { align-items: flex-start; gap: 14px; }
                    .influencer-hero-icon { width: 42px; height: 42px; border-radius: 12px; }
                    .influencer-hero-heading h1 { font-size: 20px; }
                    .influencer-hero-copy p { font-size: 13px; }
                    .influencer-hero-platforms { margin-top: 16px; gap: 8px; }
                    .influencer-hero-platform-pill { font-size: 11.5px; padding: 5px 10px; }
                }

                .influencer-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                .influencer-btn-primary {
                    padding: 8px 16px;
                    border-radius: 10px;
                    background: #F6255A;
                    color: #fff;
                    border: none;
                    font-weight: 600;
                    cursor: pointer;
                    height: 44px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    font-size: 15px;
                    font-family: Rubik, sans-serif;
                    white-space: nowrap;
                    transition: background 0.15s ease;
                }
                .influencer-btn-primary:hover { background: #e11d4c; }
                .influencer-btn-outline {
                    background: rgba(246,37,90,0.1);
                    border: 1px solid #F6255A;
                    color: #F6255A;
                    padding: 12px 32px;
                    border-radius: 12px;
                    font-weight: 600;
                    font-family: Rubik, sans-serif;
                    cursor: pointer;
                    transition: background 0.15s ease;
                }
                .influencer-btn-outline:hover { background: rgba(246,37,90,0.18); }
                .influencer-btn-ghost {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(255,255,255,0.05);
                    border: 1px solid rgba(255,255,255,0.08);
                    color: #e8e8e8;
                    padding: 8px 14px;
                    border-radius: 10px;
                    font-weight: 600;
                    font-size: 13px;
                    font-family: Rubik, sans-serif;
                    cursor: pointer;
                    transition: background 0.15s ease;
                    white-space: nowrap;
                }
                .influencer-btn-ghost:hover:not(:disabled) { background: rgba(255,255,255,0.1); }
                .influencer-btn-ghost:disabled { opacity: 0.45; cursor: not-allowed; }
                .influencer-select-trigger {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: rgba(255,255,255,0.05);
                    border: 1px solid rgba(255,255,255,0.08);
                    color: #e8e8e8;
                    padding: 8px 12px;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 600;
                    font-family: Rubik, sans-serif;
                    cursor: pointer;
                    white-space: nowrap;
                    transition: background 0.15s ease;
                }
                .influencer-select-trigger:hover { background: rgba(255,255,255,0.1); }
                .influencer-select-menu {
                    position: absolute;
                    top: calc(100% + 6px);
                    right: 0;
                    min-width: 190px;
                    background: #21222d;
                    border: 1px solid rgba(255,255,255,0.1);
                    border-radius: 12px;
                    box-shadow: 0 12px 32px rgba(0,0,0,0.45);
                    padding: 6px;
                    z-index: 20;
                }
                .influencer-select-option {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    padding: 9px 10px;
                    border-radius: 8px;
                    font-size: 13px;
                    font-family: Rubik, sans-serif;
                    color: #e8e8e8;
                    cursor: pointer;
                }
                .influencer-select-option:hover { background: rgba(255,255,255,0.06); }
                .influencer-select-option.is-active { color: #fff; background: rgba(246,37,90,0.14); }
            `}</style>
        </React.Fragment>
    )
}

export default InfluencerExplorer
